import React from "react"
import type { OPLayoutComponent } from "connected-components/op-layout-engine/types"
import { getModuleComponentName } from "connected-components/op-layout-engine/panel-id-utils"
import { getModuleDefinition, isModuleTypeRegistered } from "../modules/registry"
import { MODULE_TYPES, ModuleRenderDependencies, DashboardModule, ModuleType } from "../modules/types"
import { PanelIdProvider, PanelTitleProvider } from "../context/panel-id"
import { downloadRegistry } from "../context/download-registry"
import { warn } from "helpers/dev-logger"

type ModuleRenderer = (dependencies: ModuleRenderDependencies, moduleId: string) => React.ReactElement

interface CreateComponentDependencies extends ModuleRenderDependencies {
  onRemoveModule?: (moduleId: string) => void
  onUpdateModuleTitle?: (moduleId: string, newTitle: string) => void
}

const MODULE_RENDERERS: Record<ModuleType, ModuleRenderer> = {
  [MODULE_TYPES.ASSET_VIEW]: (dependencies) => {
    const AssetViewWrapper = require("connected-components/asset-view").default
    return (
      <div className="dv-panel-asset-view">
        <AssetViewWrapper
          context={dependencies.TradeContext}
          instrumentsWithMetadata={dependencies.instrumentsWithMetadata}
          hasMultipleInstruments={dependencies.hasMultipleInstruments}
          contentSets={dependencies.contentSets}
        />
      </div>
    )
  },

  [MODULE_TYPES.KPI]: (dependencies) => {
    const KPIWrapper = require("../../kpi").default
    return (
      <div className="dv-panel-kpi">
        <KPIWrapper
          context={dependencies.TradeContext}
          instrumentsWithMetadata={dependencies.instrumentsWithMetadata}
          contentSets={dependencies.contentSets}
        />
      </div>
    )
  },

  [MODULE_TYPES.GRAPH]: (dependencies) => {
    const GraphWrapper = require("../../graph").default
    return (
      <div className="dv-panel-graph">
        <GraphWrapper
          context={dependencies.TradeContext}
          instrumentsWithMetadata={dependencies.instrumentsWithMetadata}
          modelMetadata={dependencies.modelMetadata}
        />
      </div>
    )
  },

  [MODULE_TYPES.FACTORS]: (dependencies) => {
    const FactorsViewWrapper = require("connected-components/factors-view").default
    return (
      <div className="dv-panel-factors">
        <FactorsViewWrapper
          context={dependencies.TradeContext}
          instrumentsWithMetadata={dependencies.instrumentsWithMetadata}
          contentSets={dependencies.contentSets}
        />
      </div>
    )
  },

  [MODULE_TYPES.AI_ARTIFACT]: (dependencies, moduleId) => {
    const AiArtifactModule = require("../modules/ai-artifact").default
    return (
      <div className="dv-panel-ai-artifact">
        <AiArtifactModule {...dependencies} moduleId={moduleId} />
      </div>
    )
  },
}

function createModuleComponent(
  module: DashboardModule,
  dependencies: CreateComponentDependencies,
): OPLayoutComponent | null {
  const { type, config } = module

  if (!isModuleTypeRegistered(type)) {
    warn(`Module type "${type}" is not registered, skipping render`)
    return null
  }

  const renderer = MODULE_RENDERERS[type]
  if (!renderer) {
    warn(`No renderer found for module type: ${type}`)
    return null
  }

  const definition = getModuleDefinition(type)

  const renderDependencies: ModuleRenderDependencies = {
    ...dependencies,
    config,
  }

  const title = config.title || definition.title

  return {
    component: getModuleComponentName(type, module.id),
    title,
    icon: definition.icon,
    renderer: (props) => (
      <PanelIdProvider value={props.api.id}>
        <PanelTitleProvider value={title}>
          {renderer(renderDependencies, module.id)}
        </PanelTitleProvider>
      </PanelIdProvider>
    ),
    removable: definition.removable,
    removePanel: definition.removable && dependencies.onRemoveModule
      ? () => {
        dependencies.onRemoveModule?.(module.id)
      }
      : undefined,
    editable: definition.editable,
    onUpdateTitle: definition.editable && dependencies.onUpdateModuleTitle
      ? (panelId: string, newTitle: string) => {
        dependencies.onUpdateModuleTitle?.(module.id, newTitle)
        downloadRegistry.updateSheetNamesForPanel(panelId, newTitle)
      }
      : undefined,
  }
}

export function createModuleComponents(
  modules: DashboardModule[],
  dependencies: CreateComponentDependencies,
): OPLayoutComponent[] {
  return modules
    .map((module) => createModuleComponent(module, dependencies))
    .filter((component): component is OPLayoutComponent => component !== null)
}
